import moment, { Moment } from "moment";
import {ConfigurationsType} from "../types/configurations";
import {getMinDaysOffsetBy, isHandleExternalSearchField, isUpdateResultsSearchAvailable} from "./configurationHelper";
import {getUserProfileType} from './userHelper'
import { ShapeType } from "../types/shape";
import {ProductImageStacType} from "../types/product";
import { AppParametersType } from "../types/app";
import translations from "../translations";

export const setTimeToDate = (date, time = {}) => {
  return date.clone().set({
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
    ...time
  });
};

const originDate = (): Moment => {
  return moment.utc([2000]);
};

const earlyDate = (date: Moment): Moment => {
  return setTimeToDate(date);
};

const lateDate = (date: Moment): Moment => {
  return setTimeToDate(date, {
    hours: 23,
    minutes: 59,
    seconds: 59,
    milliseconds: 59,
  });
};

const lateYesterday = (sysdateUTC) => {
  return lateDate(sysdateUTC.clone().subtract(1, "days"));
};

const earlyTomorrow = (sysdateUTC) => {
  return lateDate(sysdateUTC.clone().add(1, "days"));
};

const earlyOriginDate = () => {
  return earlyDate(originDate());
};

const plusYearsFrom = (
  momentDate: Moment,
  addedYears: number
) => {
  return momentDate.clone().add(addedYears, "years");
};

const plusDaysFrom = (
  momentDate: Moment,
  addedDays: number
) => {
  return momentDate.clone().add(addedDays, "days");
};

const plusHoursFrom = (
  momentDate: Moment,
  addedHours: number
) => {
  return momentDate.clone().add(addedHours, "hours");
};

export const sysDateUTC = (): Moment => {
  // TODO: prendere da servizio esterno
  return moment.utc();
};

const datesAreOnSameDay = (
  firstDate: Moment,
  secondDate: Moment
): boolean => {
  return firstDate.year() === secondDate.year() &&
    firstDate.month() === secondDate.month() &&
    firstDate.date() === secondDate.date();
};

export const getMinTimeLimit = (
  pickedDate: Date,
  minDateLimit: Date,
): Date => {
  const pickedMoment = dateToMoment(pickedDate);
  const minMomentLimit = dateToMoment(minDateLimit);
  return datesAreOnSameDay(pickedMoment, minMomentLimit)
    ? minDateLimit
    : new Date(1982, 1, 1, 0, 0, 0, 0);
};

export const getMaxTimeLimit = (
  pickedDate: Date,
  maxDateLimit: Date,
): Date => {
  const pickedMoment = dateToMoment(pickedDate);
  const maxMomentLimit = dateToMoment(maxDateLimit);
  return datesAreOnSameDay(pickedMoment, maxMomentLimit)
    ? maxDateLimit
    : new Date(1982, 1, 1, 23, 59, 59, 59);
};

interface DateLimitsType {
  minDate: Date,
  maxDate: Date,
}

export const getDatePickerLimits = (
  appParameters: AppParametersType,
  configurations: ConfigurationsType,
  scopeFields: string,
  formValues: any,
  areaSelected: ShapeType,
  selectedProducts: ProductImageStacType[],
  isFromDate: boolean
) : DateLimitsType => {
  const {
    minDate,
    maxDate,
  } = getMomentLimitsFromConfigurations(appParameters, configurations, scopeFields, formValues, areaSelected, selectedProducts, isFromDate);

  return {
    minDate: fromMomentToDate(minDate) as Date,
    maxDate: fromMomentToDate(maxDate) as Date,
  };
};

export const ISOStringToDate = (
  strISODate
): Date | null => {
  return strISODate
    ? parseISOString(strISODate)
    : null;
};

function parseISOString(s) {
  const b = s.split(/\D+/);
  return new Date(
    parseInt(b[0]),
    parseInt(b[1]) - 1,
    parseInt(b[2]),
    parseInt(b[3]),
    parseInt(b[4]),
    parseInt(b[5]),
    parseInt(b[6])
  );
}

interface MomentLimitsType {
  minDate: Moment,
  maxDate: Moment,
}

export const getMomentLimitsFromConfigurations = (
  appParameters: AppParametersType,
  configurations: ConfigurationsType,
  scopeFields: string,
  formValues: any,
  selectedArea: ShapeType,
  selectedProducts: ProductImageStacType[],
  isFromDate: boolean
) : MomentLimitsType => {
  const sysdateUTC = sysDateUTC();
  const fromDate = fromISOStringToUTCMoment(formValues.date1);
  const toDate = fromISOStringToUTCMoment(formValues.date2);
  

  //BYPASS DG FROM CHECK DAYSOFFSET
  const daysOffset = formValues.missions && formValues.missions != '030' ? getMinDaysOffsetBy(configurations, appParameters, scopeFields, formValues, selectedArea) : 0;
  //const daysOffset = getMinDaysOffsetBy(configurations, scopeFields, formValues, selectedArea);
  
  let dateLimits;
  if (isFromDate) {
    dateLimits = getFromMomentLimitsFromConfigurations(sysdateUTC, fromDate, toDate, formValues, daysOffset);
  } else {
    dateLimits = getToMomentLimitsFromConfigurations(sysdateUTC, fromDate, toDate, formValues, daysOffset);
  }
  if (isUpdateResultsSearchAvailable(configurations, appParameters, formValues, selectedProducts)) {
    dateLimits = getUpdateResultsSearchMomentLimitsFromConfigurations(sysdateUTC, fromDate, toDate, formValues, dateLimits, isFromDate);
  }

  return dateLimits;
};

const getFromMomentLimitsFromConfigurations = (
  sysdateUTC: Moment,
  fromDate: Moment | null,
  toDate: Moment | null,
  formValues,
  daysOffset: number = 0
) : MomentLimitsType => {
  let minDate;
  let maxDate;

  // @mapitaly: la data sarà sempre nel passato
  // if (formValues.routine) {
  minDate = earlyDate(originDate());
  // @mapitaly: la data massima sarà il momento del controllo
  maxDate = sysdateUTC.clone(); // was: maxDate = lateYesterday(sysdateUTC);
  // } else {
  //   minDate = earlyTomorrow(sysdateUTC);
  //   maxDate = ondemandMaxDateLimitFrom(sysdateUTC, fromDate, daysOffset);
  // }

  if (!!toDate) {
    maxDate = toDate.clone();
  }

  return {
    minDate,
    maxDate,
  }
};


const getToMomentLimitsFromConfigurations = (
  sysdateUTC: Moment,
  fromDate: Moment | null,
  toDate: Moment | null,
  formValues,
  daysOffset: number = 0
) : MomentLimitsType => {
  let minDate;
  let maxDate;

  // @mapitaly: la data sarà sempre nel passato
  // if (formValues.routine) {
  minDate = earlyOriginDate();
  maxDate = sysdateUTC.clone();
  // } else {
  //   minDate = daysOffset > 0
  //     ? plusDaysFrom(sysdateUTC, daysOffset + 1)
  //     : earlyTomorrow(sysdateUTC);
  //   maxDate = ondemandMaxDateLimitFrom(sysdateUTC, toDate, daysOffset);
  // }

  if (!!fromDate) {
    minDate = daysOffset > 0
      ? fromDate.clone().add(daysOffset, "days")
      : fromDate.clone();
  }

  return {
    minDate,
    maxDate,
  }
};

const getUpdateResultsSearchMomentLimitsFromConfigurations = (
  sysdateUTC: Moment,
  fromDate: Moment | null,
  toDate: Moment | null,
  formValues,
  defaultDateLimits: MomentLimitsType,
  isFromDate: boolean
) : MomentLimitsType => {
  let minDate = defaultDateLimits.minDate;
  let maxDate = defaultDateLimits.maxDate;

  if (isFromDate && !!fromDate) {
    maxDate = fromDate;
  } else if (!isFromDate && !!toDate) {
    minDate = toDate;
  }

  return {
    minDate,
    maxDate,
  }
};

export const dateToMoment = (
  date: Date
): Moment => {
  return moment.utc([
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes()
  ]);
};

export const fromMomentToDate = (
  momentDate: Moment
): Date | null => {
  return momentDate
    ? parseISOString(momentDate.toISOString())
    : null;
};

export const fromDateToISOString = (
  date: Date
): string => {
  return date
    ? dateToMoment(date).toISOString()
    : "";
};

export const fromISOStringToUTCMoment = (
  strDate: string
): Moment | null => {
  return strDate
    ? moment.utc(strDate)
    : null;
};

export const areCorrectDates = (
  strFromDate: string,
  strToDate: string
) => {
  const fromDate = fromISOStringToUTCMoment(strFromDate);
  const toDate = fromISOStringToUTCMoment(strToDate);
  return fromDate && toDate && fromDate.isAfter(toDate);
};

export const getMomentDaysOffset = (
  fromDate: Moment | null,
  toDate: Moment | null
): number => {
  return fromDate && toDate
    ? toDate.diff(fromDate, "days")
    : 0
};

export const areDatesValid = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  formValues,
  selectedArea: ShapeType
): boolean => {
  const fromDate = fromISOStringToUTCMoment(formValues.date1);
  const toDate = fromISOStringToUTCMoment(formValues.date2);

  //BYPASS DG FROM CHECK DAYSOFFSET
  const minDaysOffset = formValues.missions && formValues.missions != '030' ? getMinDaysOffsetBy(configurations, appParameters, scopeFields, formValues, selectedArea) : 0;
  //const minDaysOffset = getMinDaysOffsetBy(configurations, scopeFields, formValues, selectedArea);
  //const currentDaysOffset = getMomentDaysOffset(fromDate, toDate);
  const currentDaysOffset = formValues.missions && formValues.missions != '030' ? getMomentDaysOffset(fromDate, toDate) : 0;

  return !fromDate || !toDate || currentDaysOffset >= minDaysOffset;
};

export const onPickISOStringDate = (
  newPickedDate: Date,
  lastPickedDate: Date | null,
  minDate: Date,
  maxDate: Date,
): string => {
  const currentMoment = dateToMoment(newPickedDate);
  const minMoment = dateToMoment(minDate);
  const maxMoment = dateToMoment(maxDate);

  let pickedMoment: Moment = currentMoment.clone();
  if (!isMomentIncludeIn(currentMoment, minMoment, maxMoment)) {
    pickedMoment = currentMoment.isBefore(minMoment)
      ? minMoment
      : maxMoment
    // // when the user clicks on time
    if (!lastPickedDate && (currentMoment.hours() > 0 || currentMoment.minutes() > 0 || currentMoment.milliseconds() > 0)) {
      pickedMoment
        .hours(currentMoment.hours())
        .minutes(currentMoment.minutes())
        .seconds(currentMoment.seconds())
        .milliseconds(currentMoment.milliseconds())
    }
  }

  return pickedMoment.toISOString();
}

export const isMomentIncludeIn = (
  currentMoment: Moment,
  minMoment: Moment,
  maxMoment: Moment
) => {
  return currentMoment.isBetween(
    minMoment.clone().subtract(1, "milliseconds"),
    maxMoment.clone().add(1, "milliseconds")
  );
}
